"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState, useRef } from "react";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";
import { useLightbox } from "@/hooks/useLightbox";

interface SocialActivityImage {
  url: string;
  title: string;
}

interface ProcessedSocialActivity {
  title: string;
  category: string;
  images: SocialActivityImage[];
}

interface SocialActivitiesClientProps {
  activities: ProcessedSocialActivity[];
  cms?: Record<string, string>;
}

// Carousel component for each section with smooth Instagram-style scrolling
function SocialCarousel({
  images,
  alt,
}: {
  images: SocialActivityImage[];
  alt: string;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleClose = () => setLightboxOpen(false);

  // Use the reusable lightbox hook for scroll lock, escape, and popstate
  useLightbox(lightboxOpen, handleClose);

  // Keyboard navigation (arrow keys)
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, images.length]);

  // Scroll to active image with smooth animation
  const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.current) return;
    isProgrammaticScrollRef.current = true;
    const container = scrollContainerRef.current;
    const scrollWidth = container.clientWidth;
    container.scrollTo({
      left: index * scrollWidth,
      behavior: 'smooth'
    });
    
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
  };

  // Update active index based on scroll position with debouncing
  useEffect(() => {
    if (!lightboxOpen || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    
    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.clientWidth;
        const calculatedIndex = Math.round(scrollLeft / scrollWidth);
        
        if (calculatedIndex !== activeIndex && 
            calculatedIndex >= 0 && 
            calculatedIndex < images.length) {
          setActiveIndex(calculatedIndex);
        }
      }, 150);
    };

    const handleScrollEnd = () => {
      if (isProgrammaticScrollRef.current) return;
      
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.clientWidth;
      const calculatedIndex = Math.round(scrollLeft / scrollWidth);
      
      if (calculatedIndex !== activeIndex && 
          calculatedIndex >= 0 && 
          calculatedIndex < images.length) {
        setActiveIndex(calculatedIndex);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('scrollend', handleScrollEnd, { passive: true } as AddEventListenerOptions);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('scrollend', handleScrollEnd);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [lightboxOpen, activeIndex, images.length]);

  // Scroll to active image when it changes (from button clicks)
  useEffect(() => {
    if (lightboxOpen && scrollContainerRef.current) {
      scrollToIndex(activeIndex);
    }
  }, [activeIndex, lightboxOpen]);

  // Initial scroll position when lightbox opens
  useEffect(() => {
    if (lightboxOpen && scrollContainerRef.current) {
      isProgrammaticScrollRef.current = true;
      const container = scrollContainerRef.current;
      const scrollWidth = container.clientWidth;
      container.scrollLeft = activeIndex * scrollWidth;
      
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 100);
    }
    // Only run when lightbox opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // Initial preload when lightbox opens - load a batch of images
  useEffect(() => {
    if (!lightboxOpen || images.length === 0) return;
    
    const preloadImage = (src: string, priority: boolean = false) => {
      const img = new window.Image();
      if (priority && 'fetchPriority' in img) {
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'high';
      }
      img.src = src;
    };
    
    // Preload current image with high priority
    if (images[activeIndex]) preloadImage(images[activeIndex].url, true);
    
    // On lightbox open, aggressively preload 15 images ahead and 15 behind for instant navigation
    for (let i = 1; i <= 15; i++) {
      const nextIdx = (activeIndex + i) % images.length;
      const prevIdx = (activeIndex - i + images.length) % images.length;
      // First 5 images get high priority
      const isHighPriority = i <= 5;
      if (images[nextIdx]) preloadImage(images[nextIdx].url, isHighPriority);
      if (images[prevIdx]) preloadImage(images[prevIdx].url, isHighPriority);
    }
    // Only run when lightbox opens, not on activeIndex change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // Preload adjacent images in lightbox for faster navigation
  useEffect(() => {
    if (!lightboxOpen || images.length === 0) return;

    const preloadImage = (src: string, priority: boolean = false) => {
      const img = new window.Image();
      if (priority && 'fetchPriority' in img) {
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'high';
      }
      img.src = src;
    };

    // Aggressively preload next 8 and previous 8 images for instant navigation
    const imagesToPreload: { src: string; priority: boolean }[] = [];
    
    // Preload next images (first 3 with high priority)
    for (let i = 1; i <= 8; i++) {
      const nextIndex = (activeIndex + i) % images.length;
      if (images[nextIndex]) {
        imagesToPreload.push({ src: images[nextIndex].url, priority: i <= 3 });
      }
    }
    
    // Preload previous images (first 3 with high priority)
    for (let i = 1; i <= 8; i++) {
      const prevIndex = (activeIndex - i + images.length) % images.length;
      if (images[prevIndex] && prevIndex !== activeIndex) {
        imagesToPreload.push({ src: images[prevIndex].url, priority: i <= 3 });
      }
    }
    
    // Preload all images in parallel with appropriate priority
    imagesToPreload.forEach(({ src, priority }) => preloadImage(src, priority));
  }, [lightboxOpen, activeIndex, images]);

  if (!images || images.length === 0) {
    return (
      <div className="w-full max-w-50 mx-auto relative block">
        <div className="w-50 h-50 bg-gray-200 rounded flex items-center justify-center">
          <p className="text-gray-500 text-sm">No images</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Show only the first image */}
      <button
        className="w-full max-w-50 mx-auto relative block"
        onClick={() => {
          setActiveIndex(0);
          setLightboxOpen(true);
        }}
        aria-label={`View ${alt} gallery`}
      >
        <Image
          src={images[0].url}
          alt={`${alt} 1`}
          width={200}
          height={200}
          className="rounded object-cover"
          sizes="(max-width: 639px) 120px, (max-width: 1023px) 160px, 200px"
        />
      </button>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-9999 bg-black bg-opacity-80 transition-opacity duration-300"
          onClick={handleClose}
        >
          {/* Top blue bar - responsive */}
          <div
            className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-3 md:p-4 bg-school-navy"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-white text-base md:text-lg font-medium">
              {activeIndex + 1} of {images.length}
            </div>
            <button
              className="text-white text-3xl md:text-4xl font-bold hover:text-yellow-400 transition-colors p-1 md:p-2"
              onClick={handleClose}
              aria-label="Close gallery"
            >
              ×
            </button>
          </div>

          {/* Horizontal scroll container with snap - Instagram/Facebook style */}
          <div
            ref={scrollContainerRef}
            className="gallery-scroll-container absolute inset-0 pt-16 pb-20 md:pb-16 lg:pb-14 overflow-x-auto overflow-y-hidden"
          >
            <div className="flex h-full" style={{ width: `${images.length * 100}vw` }}>
              {images.map((image, idx) => (
                <div
                  key={`lightbox-${idx}`}
                  className="gallery-scroll-item shrink-0 flex items-center justify-center px-2"
                  style={{ width: '100vw', height: '100%' }}
                  onClick={handleClose}
                >
                  <div
                    className="relative max-w-full max-h-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                      width: 'fit-content',
                      height: 'fit-content',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  >
                    {/* Use native img for instant loading with preloaded images */}
                    <img
                      src={image.url}
                      alt={`${alt} ${idx + 1}`}
                      className="max-w-full max-h-full w-auto h-auto object-contain rounded"
                      loading={Math.abs(idx - activeIndex) <= 5 ? 'eager' : 'lazy'}
                      fetchPriority={Math.abs(idx - activeIndex) <= 3 ? 'high' : 'low'}
                      style={{ maxHeight: 'calc(100vh - 10rem)' }}
                      decoding="async"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom blue bar - reorganized layout for larger screens */}
          <div
            className="absolute bottom-0 left-0 right-0 z-10 p-2 md:p-3 bg-school-navy"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile/tablet layout: thumbnails above buttons */}
            <div className="lg:hidden">
              {/* Compact thumbnail strip */}
              <div className="flex gap-1 md:gap-2 justify-center overflow-x-auto pb-1 md:pb-2 mb-2">
                {(() => {
                  // Show fewer thumbnails on mobile (6), more on larger screens (10)
                  const maxThumbs = 10; // We'll handle mobile with CSS hiding
                  const startIdx = Math.max(
                    0,
                    activeIndex - Math.floor(maxThumbs / 2),
                  );
                  const endIdx = Math.min(images.length, startIdx + maxThumbs);
                  const visibleImages = images.slice(startIdx, endIdx);

                  return visibleImages.map((img, idx) => {
                    const actualIdx = startIdx + idx;
                    return (
                      <button
                        key={actualIdx}
                        className={`w-10 h-10 md:w-12 md:h-12 relative border-2 shrink-0 ${
                          activeIndex === actualIdx
                            ? "border-yellow-400"
                            : "border-transparent"
                        } rounded overflow-hidden hover:border-yellow-200 transition-colors ${
                          idx >= 6 ? "hidden sm:block" : "" // Hide thumbnails 7-10 on mobile
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIndex(actualIdx);
                        }}
                        aria-label={`View ${alt} ${actualIdx + 1}`}
                      >
                        <Image
                          src={img.url}
                          alt={`${alt} ${actualIdx + 1}`}
                          fill
                          className="object-cover"
                          sizes="(max-width: 640px) 40px, 48px"
                        />
                      </button>
                    );
                  });
                })()}

                {/* Show indicator if there are more images - responsive text */}
                {images.length > 6 && (
                  <div className="flex items-center px-2 md:px-3 text-gray-300 text-xs md:text-sm whitespace-nowrap">
                    <span className="sm:hidden">+{images.length - 6} more</span>
                    <span className="hidden sm:inline">
                      +{images.length - 10} more
                    </span>
                  </div>
                )}
              </div>

              {/* Navigation buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-between w-full">
                <button
                  className="w-full sm:w-auto text-black text-sm md:text-base px-3 md:px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex(
                      (activeIndex - 1 + images.length) % images.length,
                    );
                  }}
                  disabled={images.length <= 1}
                  aria-label="Previous image"
                >
                  ← Previous
                </button>
                <button
                  className="w-full sm:w-auto text-black text-sm md:text-base px-3 md:px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex((activeIndex + 1) % images.length);
                  }}
                  disabled={images.length <= 1}
                  aria-label="Next image"
                >
                  Next →
                </button>
              </div>
            </div>

            {/* Desktop layout: single row with thumbnails between buttons */}
            <div className="hidden lg:flex items-center justify-between gap-4">
              {/* Previous button */}
              <button
                className="text-black text-base px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex(
                    (activeIndex - 1 + images.length) % images.length,
                  );
                }}
                disabled={images.length <= 1}
                aria-label="Previous image"
              >
                ← Previous
              </button>

              {/* Thumbnails in center */}
              <div className="flex gap-2 justify-center overflow-x-auto flex-1 max-w-2xl">
                {(() => {
                  const maxThumbs = 8; // Fewer for inline layout
                  const startIdx = Math.max(
                    0,
                    activeIndex - Math.floor(maxThumbs / 2),
                  );
                  const endIdx = Math.min(images.length, startIdx + maxThumbs);
                  const visibleImages = images.slice(startIdx, endIdx);

                  return visibleImages.map((img, idx) => {
                    const actualIdx = startIdx + idx;
                    return (
                      <button
                        key={actualIdx}
                        className={`w-12 h-12 relative border-2 shrink-0 ${
                          activeIndex === actualIdx
                            ? "border-yellow-400"
                            : "border-transparent"
                        } rounded overflow-hidden hover:border-yellow-200 transition-colors`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIndex(actualIdx);
                        }}
                        aria-label={`View ${alt} ${actualIdx + 1}`}
                      >
                        <Image
                          src={img.url}
                          alt={`${alt} ${actualIdx + 1}`}
                          fill
                          className="object-cover"
                          sizes="48px"
                        />
                      </button>
                    );
                  });
                })()}

                {/* Show indicator if there are more images */}
                {images.length > 8 && (
                  <div className="flex items-center px-2 text-gray-300 text-sm whitespace-nowrap">
                    +{images.length - 8} more
                  </div>
                )}
              </div>

              {/* Next button */}
              <button
                className="text-black text-base px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((activeIndex + 1) % images.length);
                }}
                disabled={images.length <= 1}
                aria-label="Next image"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SocialActivitiesClient({
  activities,
  cms,
}: SocialActivitiesClientProps) {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Use optimized parallax hook
  const [offset, isDesktop, transform3d] = useParallaxOffset(0.2, 640, parallaxRef);
  
  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Synchronized auto-scroll for all carousels
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => prev + 1);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Helper function to get display name for categories
  const getCategoryDisplayName = (category: string) => {
    const displayNames: { [key: string]: string } = {
      "miss-mr-ashton": "Miss & Mr Ashton Johns",
      "derby-day": "Derby Day",
      "spring-dance": "Spring Dance",
      "market-day": "Market Day",
      awards: "Award Functions",
      camps: "Leadership & Adventure Camps",
      "golf-day": "Golf Day",
      concerts: "Concerts",
    };
    return displayNames[category] || category;
  };

  // Tailwind classes for dynamic parallax background
  // Use inline style only for transform, but move all other styles to Tailwind
  // For objectPosition, use Tailwind arbitrary value
  return (
    <section className="py-16 text-white relative overflow-hidden">
      {/* Background Image with Parallax */}
      <div
        ref={parallaxRef}
        className={`absolute inset-0 will-change-transform ${isDesktop ? "-top-[80%] h-[140%]" : "top-0 h-full"} w-full`}
        style={mounted && isDesktop ? { transform: transform3d } : { transform: 'translate3d(0, 0, 0)' }}
      >
        {cms?.bg_image_url && (
          <Image
            src={cms.bg_image_url}
            alt="Social activities background"
            fill
            className="object-cover object-[center_40%]"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px"
          />
        )}
      </div>
      {/* Overlay */}
      {cms?.overlay_color && (
        <div className="absolute inset-0" style={{ background: cms.overlay_color }}></div>
      )}

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-0 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          {cms?.pre_heading && (
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-2">
              {cms.pre_heading}
            </h2>
          )}
          {cms?.heading && (
            <h3 className="text-5xl sm:text-6xl md:text-7xl font-bold">
              {cms.heading}
            </h3>
          )}
          {cms?.description && (
            <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed mt-8 max-w-4xl mx-auto">
              {cms.description}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 grid-rows-4 md:grid-cols-4 md:grid-rows-none gap-y-6 gap-x-4 mb-8 px-3 sm:px-0">
          {activities.map((activity, index) => {
            const displayName = getCategoryDisplayName(activity.category);
            const images = activity.images;

            return (
              <div
                key={index}
                className="text-center flex flex-col items-center"
              >
                {/* Yellow bar with title above image, spans image width */}
                <div className="w-full max-w-40 md:max-w-50 mx-auto">
                  <div className="flex items-center justify-center w-full min-h-10 bg-school-gold rounded-t px-2 text-center md:min-h-12">
                    <span className="text-black font-bold text-sm md:text-base truncate w-full whitespace-normal leading-snug">
                      {displayName}
                    </span>
                  </div>
                </div>
                <div className="w-full max-w-40 md:max-w-50 mx-auto">
                  <SocialCarousel images={images} alt={displayName} />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Buttons */}
        {cms?.btn_text && (
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Link
            href={cms?.btn_url ?? "/contact-us"}
            className="bg-school-gold hover:bg-school-gold-dark text-black font-bold py-4 px-8 rounded text-lg transition-colors text-center"
          >
            {cms.btn_text}
            <span className="sr-only">
              Contact Ashton John's Private School
            </span>
          </Link>
        </div>
        )}
      </div>
    </section>
  );
}
